/**
 * useProfilingStatus — GET /enterprise/connectors/{conn_id}/profiling-status.
 *
 * Polls every 2s while the BE is still discovering or profiling
 * (``status === "pending" | "in_progress"`` OR ``is_discovering`` OR
 * ``is_profiling``). Once the BE settles on ``complete`` / ``no_schemas``
 * the interval drops to ``false`` so we don't burn cycles.
 *
 * Mirrors V1 ``useDiscoveryPoller``. The 2s cadence is the V1 default —
 * tuned so the table-by-table progress bar feels live without DOS-ing
 * the BE on large schemas (e.g. 200-table SAP migration).
 */

import { useQuery } from "@tanstack/react-query";

import { type ProfilingStatusResponse, getEnterpriseProfilingStatus } from "@ando/core/api";

import { enterpriseProfilingKey } from "./enterpriseKeys";

const POLL_MS = 2_000;

export interface UseProfilingStatusOptions {
  /** Disable polling entirely (e.g. dialog not open). */
  readonly enabled?: boolean;
}

function isRunning(s: ProfilingStatusResponse | undefined): boolean {
  if (!s) return false;
  return s.is_discovering || s.is_profiling || s.status === "pending" || s.status === "in_progress";
}

export function useProfilingStatus(
  connId: string,
  { enabled = true }: UseProfilingStatusOptions = {},
) {
  return useQuery({
    queryKey: enterpriseProfilingKey(connId),
    queryFn: () => getEnterpriseProfilingStatus(connId),
    enabled: enabled && connId.length > 0,
    refetchInterval: (query) => (isRunning(query.state.data) ? POLL_MS : false),
    retry: 1,
  });
}
